import { useEffect, useRef, useCallback, useState } from "react";
import AuthProvider from "./authService";
import { HubConnectionState } from "@microsoft/signalr";
import { closeConnection } from "../utility/Dashboard/utility";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "../store/combinedReducer";
import { tokenGettingRefreshed } from "../../features/components/academy/create-course/store/create-course.action";

const REFRESH_LOCK_KEY = "token_refresh_lock";
const ACTIVE_TAB_KEY = "active_tab_id";
const REFRESH_IN_PROGRESS_KEY = "token_refresh_in_progress";
const LAST_ACTIVITY_KEY = "last_tab_activity";
const LOG_PREFIX = "[TokenAutoRefresh]";
const LOCK_TIMEOUT = 30000;
const BUFFER_TIME = 90 * 1000;
const INACTIVE_THRESHOLD = 5000; // 5 seconds

export const useTokenAutoRefresh = () => {
  const dispatch = useDispatch();
  const timeoutIdRef = useRef<number | null>(null);
  const logIntervalIdRef = useRef<number | null>(null);
  const isRefreshingRef = useRef(false);
  const tabId = useRef(`tab_${Date.now()}_${Math.random()}`);
  const [isPageVisible, setIsPageVisible] = useState(!document.hidden);
  const [isTabFocused, setIsTabFocused] = useState(true);
  const signalRConnection = useSelector(
    (state: AppState) => state.reducer.signalR
  );

  const acquireLock = () => {
    const now = Date.now();
    const lockData = JSON.parse(
      localStorage.getItem(REFRESH_LOCK_KEY) || "null"
    );

    if (!lockData || now - lockData.timestamp > LOCK_TIMEOUT) {
      localStorage.setItem(
        REFRESH_LOCK_KEY,
        JSON.stringify({ timestamp: now, tabId: tabId.current })
      );
      return true;
    }

    return lockData.tabId === tabId.current;
  };

  const releaseLock = () => {
    const lockData = JSON.parse(
      localStorage.getItem(REFRESH_LOCK_KEY) || "null"
    );
    if (lockData?.tabId === tabId.current) {
      localStorage.removeItem(REFRESH_LOCK_KEY);
    }
  };

  const isThisTabActive = useCallback(() => {
    const activeTabId = localStorage.getItem(ACTIVE_TAB_KEY);
    const visible = !document.hidden;
    const isActive = (activeTabId === tabId.current) && visible;
    return isActive;
  }, []);

  const isAnyTabActive = useCallback(() => {
    const lastActivity = localStorage.getItem(LAST_ACTIVITY_KEY);
    if (!lastActivity) return false;
    const timeSinceActivity = Date.now() - Number(lastActivity);
    return timeSinceActivity < INACTIVE_THRESHOLD;
  }, []);

  const scheduleRefresh = useCallback(() => {
    if (timeoutIdRef.current) clearTimeout(timeoutIdRef.current);

    const expiryTimeRaw = localStorage.getItem("token_expiry_time");
    const expiryTime = Number(expiryTimeRaw);
    const now = Date.now();

    if (!expiryTimeRaw || isNaN(expiryTime) || expiryTime <= now) {
      timeoutIdRef.current = window.setTimeout(scheduleRefresh, 5000);
      return;
    }

    const refreshDelay = Math.max(expiryTime - now - BUFFER_TIME, 1000);

    timeoutIdRef.current = window.setTimeout(async () => {
      if (isRefreshingRef.current) return;

      // Check if another tab is already refreshing
      if (localStorage.getItem(REFRESH_IN_PROGRESS_KEY) === "true") {
        timeoutIdRef.current = window.setTimeout(scheduleRefresh, 3000);
        return;
      }

      // Check if this tab is active OR if no tab is active (fallback)
      const thisTabActive = isThisTabActive();
      const anyTabActive = isAnyTabActive();

      if (!thisTabActive && anyTabActive) {
        timeoutIdRef.current = window.setTimeout(scheduleRefresh, 2000);
        return;
      }


      if (!acquireLock()) {
        timeoutIdRef.current = window.setTimeout(scheduleRefresh, 2000);
        return;
      }

      // Double-check after short delay and verify no other tab started refreshing
      await new Promise((res) => setTimeout(res, 500));

      if (localStorage.getItem(REFRESH_IN_PROGRESS_KEY) === "true") {
        releaseLock();
        timeoutIdRef.current = window.setTimeout(scheduleRefresh, 3000);
        return;
      }
      const lockData = JSON.parse(
        localStorage.getItem(REFRESH_LOCK_KEY) || "null"
      );
      if (lockData?.tabId !== tabId.current) {
        timeoutIdRef.current = window.setTimeout(scheduleRefresh, 2000);
        return;
      }

      const isLoggedIn =
        localStorage.getItem("logged_user") === JSON.stringify(true);
      if (isLoggedIn) {
        isRefreshingRef.current = true;
        localStorage.setItem(REFRESH_IN_PROGRESS_KEY, "true");
        try {
          dispatch(tokenGettingRefreshed(true));
          closeConnection(signalRConnection, HubConnectionState, dispatch);
          await AuthProvider().getRefreshToken();

          localStorage.removeItem(REFRESH_IN_PROGRESS_KEY);

          window.dispatchEvent(
            new CustomEvent("custom-storage", {
              detail: { key: "token_expiry_time", value: expiryTimeRaw },
            })
          );

        } catch (error: any) {
          localStorage.removeItem(REFRESH_IN_PROGRESS_KEY);

          if (error?.response?.data?.error === "invalid_grant") {
            timeoutIdRef.current = window.setTimeout(scheduleRefresh, 5000);
          } else {
            timeoutIdRef.current = window.setTimeout(scheduleRefresh, 10000);
          }
        } finally {
          localStorage.removeItem(REFRESH_IN_PROGRESS_KEY);
          releaseLock();
          isRefreshingRef.current = false;
        }
      }
    }, refreshDelay);
  }, [signalRConnection, isThisTabActive, isAnyTabActive]);

  const startStatusLogger = useCallback(() => {
    if (logIntervalIdRef.current) clearInterval(logIntervalIdRef.current);

    logIntervalIdRef.current = window.setInterval(() => {
      const expiryTimeRaw = localStorage.getItem("token_expiry_time");
      if (!expiryTimeRaw) return;

      const expiryTime = Number(expiryTimeRaw);
      const now = Date.now();
      const timeUntilRefresh = Math.max(expiryTime - now - BUFFER_TIME, 0);

      if (timeUntilRefresh > 0) {
        const minutes = Math.floor(timeUntilRefresh / 60000);
        const seconds = Math.floor((timeUntilRefresh % 60000) / 1000);
        // console.log(
        //   `${LOG_PREFIX} Time until next refresh: ${minutes}m ${seconds}s`
        // );
      } else {
        // console.log(`${LOG_PREFIX} Token expired or refresh is due.`);
      }
    }, 1000);
  }, []);

  useEffect(() => {
    const handleVisibilityChange = () => {
      const visible = !document.hidden;
      setIsPageVisible(visible);
      if (visible) {
        localStorage.setItem(ACTIVE_TAB_KEY, tabId.current);
        localStorage.setItem(LAST_ACTIVITY_KEY, Date.now().toString());
        scheduleRefresh();
      }
    };

    const handleFocus = () => {
      setIsTabFocused(true);
      localStorage.setItem(ACTIVE_TAB_KEY, tabId.current);
      localStorage.setItem(LAST_ACTIVITY_KEY, Date.now().toString());
      scheduleRefresh();
    };

    const handleBlur = () => {
      setIsTabFocused(false);
    };

    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === "access_token" || event.key === "token_expiry_time") {
        scheduleRefresh();
        startStatusLogger();
      } else if (event.key === REFRESH_LOCK_KEY) {
        scheduleRefresh();
        startStatusLogger();
      }
    };

    const handleCustomStorageChange = (event: Event) => {
      const customEvent = event as CustomEvent;
      if (customEvent.detail?.key === "token_expiry_time" || customEvent.detail?.key === "access_token") {
        scheduleRefresh();
        startStatusLogger();
      }
    };

    const handleBeforeUnload = () => {
      releaseLock();
      if (isThisTabActive()) {
        localStorage.removeItem(ACTIVE_TAB_KEY);
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleFocus);
    window.addEventListener("blur", handleBlur);
    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("custom-storage", handleCustomStorageChange);
    window.addEventListener("beforeunload", handleBeforeUnload);

    // Set initial active state
    localStorage.setItem(ACTIVE_TAB_KEY, tabId.current);
    localStorage.setItem(LAST_ACTIVITY_KEY, Date.now().toString());

    scheduleRefresh();
    startStatusLogger();

    return () => {
      if (timeoutIdRef.current) clearTimeout(timeoutIdRef.current);
      if (logIntervalIdRef.current) clearInterval(logIntervalIdRef.current);

      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleFocus);
      window.removeEventListener("blur", handleBlur);
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("custom-storage", handleCustomStorageChange);
      window.removeEventListener("beforeunload", handleBeforeUnload);

      handleBeforeUnload();
    };
  }, [scheduleRefresh, startStatusLogger, isThisTabActive]);
};
